/* ───────────────────────────────────────────────────────────────────────────
 * Shipping Estimator — PDP widget client
 *
 * Reads `data-*` attributes on #shipping-estimator (productId, vendor, currency,
 * production min/max weeks, customer prefilled postal), calls
 * https://pro.oblist.com/api/shipping-estimate and renders a "From X" lead price
 * inline + a modal popup with all options + an optional postal Refine UI.
 *
 * Contract with the backend:
 *  - The API ALWAYS returns success:true with a sensible geo_level.
 *  - We never display an upfront form: the buyer always sees a price (even
 *    country-only for inferred locations).
 *  - destination.country_name (full human-readable name) is used for display.
 *  - geo_level === 'full'  → show "City, postal_code" inline.
 *    Otherwise            → show "Country name" only (never an inferred postal,
 *    that hurts credibility — the user can refine inside the modal).
 *  - show_refine: true     → CTA reads "Refine" and modal includes a postal input.
 *    show_refine: false    → CTA reads "See options".
 *  - notes.ddu / notes.indicative come from the API (i18n by the backend);
 *    fallback to local locales/*.json strings if the field is absent.
 *
 * Performance:
 *  - IIFE wrapped with IntersectionObserver lazy-init (rootMargin 200px) so
 *    the API call only fires when the widget enters the viewport.
 *  - Caches results in sessionStorage keyed by (productId, suffix, currency).
 *  - Skips auto-detect for bots / headless browsers.
 *
 * I18n:
 *  - All user-visible strings come from window.SHIPPING_ESTIMATOR_I18N
 *    (populated by the Liquid snippet from locales/*.json).
 * ─────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var API_URL = 'https://pro.oblist.com/api/shipping-estimate';
  var CACHE_PREFIX = 'oblist_ship_est:';
  var CURRENCY_SYMBOL = { EUR: '€', USD: '$', GBP: '£' };

  // EU member countries (single market — no customs between them).
  // Source: https://european-union.europa.eu/principles-countries-history/eu-countries_en
  // Note: UK is NOT in EU since Brexit; Switzerland is NOT in EU.
  var EU_ISO = [
    'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE',
    'IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE'
  ];

  // ─── DDU relevance check ───────────────────────────────────────────────
  // DDU (Delivered Duty Unpaid) only matters when a customs border is crossed.
  // We hide the DDU mention when origin and destination are both in the EU
  // (single market, no customs) or when they are the same country.
  // The frontend decides show/hide; the backend decides the textual content.
  function isDduRelevant(data) {
    var origin = (data && data.origin) || {};
    var dest   = (data && data.destination) || {};
    // Without country info on either side, default to showing (safer).
    if (!origin.country || !dest.country) return true;
    if (origin.country === dest.country) return false;
    if (EU_ISO.indexOf(origin.country) !== -1 && EU_ISO.indexOf(dest.country) !== -1) {
      return false;
    }
    return true;
  }

  // ─── Bot / headless detection ──────────────────────────────────────────
  function isBot() {
    if (typeof navigator === 'undefined') return false;
    if (navigator.webdriver === true) return true;
    var ua = navigator.userAgent || '';
    return /bot|crawl|spider|slurp|search|fetcher|preview|monitor|lighthouse|headless|phantom|puppeteer|playwright/i.test(ua);
  }

  // ─── i18n helper ────────────────────────────────────────────────────────
  //
  // Liquid's `| t` auto-escapes HTML entities (so "What's included" arrives
  // as "What&#39;s included" in the JS dictionary). We then escapeHtml() before
  // inserting into innerHTML, which would double-escape (& → &amp;) and the
  // browser would render the entity literally.
  //
  // Fix: decode entities ONCE here so `t()` always returns the user-visible
  // string. escapeHtml() then becomes the single source of truth for HTML
  // safety at the point of insertion.
  var _decodeEl = null;
  function decodeEntities(s) {
    if (!s || typeof s !== 'string') return s;
    if (s.indexOf('&') === -1) return s;
    if (!_decodeEl) _decodeEl = document.createElement('textarea');
    _decodeEl.innerHTML = s;
    return _decodeEl.value;
  }

  function t(key, fallback) {
    var dict = window.SHIPPING_ESTIMATOR_I18N || {};
    return decodeEntities((key in dict) ? dict[key] : (fallback || ''));
  }

  // ─── A/B test + GA4 tracking helpers ────────────────────────────────────
  // 50/50 split on the shipping widget:
  //   • Variant A → widget visible (control / current behavior)
  //   • Variant B → widget hidden (treatment)
  // The variant is assigned ONCE per browser via localStorage so a user
  // sees the same variant across PDPs and sessions, which is required for
  // a clean A/B measurement.
  //
  // The variant is pushed to GA4 as the `widget_variant` USER property so
  // every downstream event (view_item, add_to_cart, begin_checkout,
  // purchase) automatically carries the variant attribution. Combined with
  // our custom `view_shipping_estimate` event — fired in BOTH variants on
  // eligible PDPs — we compute ATC rate / CVR diff between A and B
  // directly in GA4 Explore (Funnel exploration, filtered by
  // widget_variant + widget_displayed).
  var VARIANT_KEY = 'oblist_widget_variant';

  function getOrAssignVariant() {
    try {
      var v = localStorage.getItem(VARIANT_KEY);
      if (v === 'A' || v === 'B') return v;
      v = Math.random() < 0.5 ? 'A' : 'B';
      localStorage.setItem(VARIANT_KEY, v);
      return v;
    } catch (_e) {
      // localStorage blocked (private mode, cookies disabled) — fall back
      // to A so users always see the widget. They won't be counted in the
      // A/B test (no stable variant), which is the correct behavior:
      // unknown variant = excluded from the experiment.
      return 'A';
    }
  }

  // GA4 measurement ID — explicit to ensure our events always route to
  // the right property regardless of how / when gtag.js initializes
  // destinations.
  //
  // Why: this store loads BOTH a GA4 Web Stream tag (G-7VC7ZXSS0L) AND a
  // Google Tag wrapper (GT-5RMLWSXX) that forwards to Google Ads
  // (MC-...). Without an explicit `send_to`, custom events were being
  // routed to the Google Ads destination instead of the GA4 property
  // and never appeared in GA4 DebugView.
  //
  // Additionally, theme.liquid declares `gtag` LOCALLY inside a
  // setTimeout (around line 1321 in current main), so `window.gtag` is
  // undefined at the page global scope. We polyfill it to push to
  // `window.dataLayer` so our events always queue — gtag.js (loaded by
  // GTM via the GA4 config tag) flushes the queue to GA4 once it sees
  // its `config` command for our measurement ID.
  var GA4_MEASUREMENT_ID = 'G-7VC7ZXSS0L';

  function ensureGtag() {
    try {
      window.dataLayer = window.dataLayer || [];
      if (typeof window.gtag !== 'function') {
        window.gtag = function () { window.dataLayer.push(arguments); };
      }
      // Re-affirm the GA4 config in case theme.liquid's delayed setTimeout
      // hasn't run yet — `send_page_view: false` avoids duplicating the
      // page_view that the theme / Shopify Customer Events pixel already
      // emits, and ensures `send_to: GA4_MEASUREMENT_ID` resolves on our
      // subsequent event calls.
      if (!window.__oblistGa4Configured) {
        window.__oblistGa4Configured = true;
        window.gtag('js', new Date());
        window.gtag('config', GA4_MEASUREMENT_ID, { send_page_view: false });
      }
    } catch (_e) { /* silent */ }
  }

  function setGaUserProperty(variant) {
    ensureGtag();
    try {
      // user_properties are set for ALL configured gtag destinations by
      // design — they don't accept `send_to` as a routing key. Including
      // it leaks `send_to` as a literal user_property in GA4 (showed up
      // as a spurious "User property: send_to = G-7VC7ZXSS0L" entry).
      window.gtag('set', 'user_properties', {
        widget_variant: variant
      });
    } catch (_e) { /* silent */ }
  }

  function gaTrack(eventName, params) {
    ensureGtag();
    try {
      // `send_to` forces routing to the GA4 property — without it the
      // event was bouncing to the Google Ads tag (tid: MC-CDF62BKER5)
      // and never reaching GA4. Spread params first so we can't shadow
      // the explicit `send_to`.
      var payload = {};
      if (params) {
        for (var k in params) {
          if (Object.prototype.hasOwnProperty.call(params, k)) {
            payload[k] = params[k];
          }
        }
      }
      payload.send_to = GA4_MEASUREMENT_ID;
      window.gtag('event', eventName, payload);
    } catch (_e) { /* silent */ }
  }

  // ─── sessionStorage cache ───────────────────────────────────────────────
  // Cached entries carry a `_cachedAt` timestamp. Entries older than the TTL
  // are treated as stale and evicted so a long-lived session (e.g. buyer
  // keeps a tab open for hours) doesn't keep serving outdated rates
  // (FX shifts, carrier surcharges, fuel adjustments). 30 min is a good
  // balance: long enough that quick PDP-to-PDP browsing hits the cache,
  // short enough that prices stay reasonably fresh.
  var CACHE_TTL_MS = 30 * 60 * 1000;

  function cacheKey(productId, suffix) { return CACHE_PREFIX + productId + ':' + suffix; }

  function getCached(productId, suffix) {
    try {
      var raw = sessionStorage.getItem(cacheKey(productId, suffix));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed && parsed._cachedAt && (Date.now() - parsed._cachedAt) < CACHE_TTL_MS) {
        return parsed;
      }
      // Stale → evict so subsequent reads don't keep parsing the same dead entry
      try { sessionStorage.removeItem(cacheKey(productId, suffix)); } catch (_e) { /* silent */ }
      return null;
    } catch (e) { return null; }
  }

  function setCached(productId, suffix, data) {
    try {
      var stamped = Object.assign({}, data, { _cachedAt: Date.now() });
      sessionStorage.setItem(cacheKey(productId, suffix), JSON.stringify(stamped));
    } catch (e) { /* quota / private mode → silent */ }
  }

  // ─── Cross-product user destination ────────────────────────────────────
  // Once the buyer has typed a postal code in the Refine UI on ANY product,
  // we remember it (per session) so the next product they visit uses the
  // same destination automatically. Matches the buyer's mental model: "I
  // told you where I want it shipped — remember it across the catalog".
  // Scoped by country: if Markets switches, the stored postal is only used
  // when the country still matches.
  var USER_DEST_KEY = 'oblist_ship_est_user_dest';

  function getUserDest() {
    try {
      var raw = sessionStorage.getItem(USER_DEST_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  // Country-and-postal are persisted independently:
  //   • Country alone is enough to remember the buyer's destination intent
  //     across pages — even without a postal, the backend can produce a
  //     country-only resolution (capital-city fallback).
  //   • Postal is captured separately when the buyer has actually completed
  //     a refine. If we receive a country-only call (postal === '' or null),
  //     we preserve the previously stored postal IF it belonged to the SAME
  //     country (otherwise it would be a mismatch — e.g. Spanish postal kept
  //     while the buyer just picked Poland).
  //
  // This fixes the bug where switching country in the modal dropdown without
  // typing a postal would lose the country choice at the next page load.
  function setUserDest(country, postal) {
    if (!country) return;
    try {
      var existing = getUserDest() || {};
      var preservedPostal = '';
      if (postal) {
        preservedPostal = postal;
      } else if (existing.country === country && existing.postal) {
        preservedPostal = existing.postal;
      }
      sessionStorage.setItem(USER_DEST_KEY, JSON.stringify({
        country: country,
        postal:  preservedPostal,
        ts:      Date.now()
      }));
    } catch (e) { /* quota / private mode → silent */ }
  }

  // ─── HTML escape ────────────────────────────────────────────────────────
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  // ─── Tier description key from option type ─────────────────────────────
  // "Standard shipping"      → "desc_standard_shipping"
  // "Select (freight)"       → "desc_select_freight"
  // "Premium (white-glove)"  → "desc_premium_white_glove"
  function descKey(type) {
    var slug = String(type || '').toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    return 'desc_' + slug;
  }

  // ─── ISO 2-letter country code → human-readable name ───────────────────
  // Uses window.SHIPPING_ESTIMATOR_COUNTRIES (populated by Liquid from
  // localization.available_countries). Falls back to the ISO code if no
  // match — better than displaying "ES" alone.
  function countryName(iso) {
    if (!iso) return '';
    var list = window.SHIPPING_ESTIMATOR_COUNTRIES || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].iso === iso) return list[i].name;
    }
    return iso;
  }

  // ───────────────────────────────────────────────────────────────────────
  // Per-widget initialization
  // ───────────────────────────────────────────────────────────────────────
  function init() {
    var container = document.getElementById('shipping-estimator');
    if (!container || container.dataset.initialized === '1') return;
    container.dataset.initialized = '1';

    var productId      = container.dataset.productId;
    var productTitle   = container.dataset.productTitle || '';
    var productUrl     = container.dataset.productUrl || '';
    var currency       = container.dataset.currency || 'EUR';
    var marketsCountry = container.dataset.marketsCountry || '';
    var minWeeks       = parseInt(container.dataset.minWeeks || '0', 10) || 0;
    var maxWeeks       = parseInt(container.dataset.maxWeeks || '0', 10) || 0;
    var vendorFallback = container.dataset.vendor || '';
    var prefillPostal  = container.dataset.prefillPostal || '';
    var isMadeToOrder  = container.dataset.mto === 'yes';
    var isVintage      = container.dataset.vintage === 'true';
    // Return policy flag — comes from product.metafields.arforproduct.arfp
    // resolved in shipping-estimator.liquid. Default: returnable (true).
    // When false, the modal's "Our promise" block swaps the standard
    // "14-day returns" commitment for a Final Sale + Buyer Protection
    // variant (see buildCommitmentsBlock).
    var isReturnable   = container.dataset.returnable !== 'false';

    // ─── A/B test variant ──────────────────────────────────────────────────
    // Assign the variant ASAP and push it to GA4 so every subsequent event
    // (including the standard Shopify-emitted view_item / add_to_cart /
    // purchase) carries the right `widget_variant` attribution.
    //
    // Variant B hides the widget entirely — container goes display:none so
    // the buyer never sees the Liquid-rendered meta row or the loading
    // spinner. We still let init() run to completion (API call + exposure
    // event firing) because we need symmetric event counting between the
    // two arms of the test: every eligible PDP fires
    // `view_shipping_estimate` regardless of variant, with the
    // `widget_displayed` parameter distinguishing the cohorts.
    var widgetVariant = getOrAssignVariant();
    setGaUserProperty(widgetVariant);
    if (widgetVariant === 'B') {
      container.style.display = 'none';
    }

    // ─── GA4 exposure event ────────────────────────────────────────────────
    // Anchor event of the funnel. Fires once per eligible PDP load (both
    // variants) with the resolved shipping context. Custom dimensions /
    // metric consumed in GA4 (configured in Admin → Custom definitions):
    //   • widget_displayed (event)        true / false
    //   • origin_country (event)          ISO-2 (e.g. NL)
    //   • destination_country (event)     ISO-2 (e.g. FR)
    //   • is_cross_border (event)         true / false
    //   • has_ddu (event)                 true / false (customs border crossed)
    //   • display_mode (event)            price / range / curator_only / fallback
    //   • vendor (event)                  product vendor
    //   • from_price (metric)             cheapest shipping cost in `currency`
    //   • widget_variant (user)           A / B (set above, attached to ALL events)
    var exposureFired = false;
    function fireExposureEvent(data) {
      if (exposureFired) return;          // never fire twice per page
      if (!data || !data.success) return; // PDP wasn't actually eligible
      exposureFired = true;

      var origin = (data.origin && data.origin.country) || '';
      var dest = (data.destination && data.destination.country) || '';
      var crossBorder = !!(origin && dest && origin !== dest);
      var dduRel = isDduRelevant(data);

      var mode;
      if (data.display_mode) {
        mode = data.display_mode;
      } else if (data.curator_only) {
        mode = 'curator_only';
      } else if (data.from_price) {
        mode = 'price';
      } else {
        mode = 'fallback';
      }

      gaTrack('view_shipping_estimate', {
        item_id: productId,
        vendor: vendorFallback || '',
        origin_country: origin,
        destination_country: dest,
        is_cross_border: crossBorder,
        has_ddu: dduRel,
        display_mode: mode,
        from_price: Number(data.from_price) || 0,
        widget_displayed: widgetVariant === 'A',
        currency: currency
      });
    }

    var resultDiv    = document.getElementById('estimator-result');
    var loadingDiv   = document.getElementById('estimator-loading');
    var loadingText  = loadingDiv ? loadingDiv.querySelector('.shipping-estimator__loading-text') : null;

    var loadingInterval = null;
    var lastData = null; // for the Refine flow inside the modal

    // ─── Currency / arrival formatting ────────────────────────────────────
    function sym() { return CURRENCY_SYMBOL[currency] || (currency + ' '); }

    function getLocale() {
      return (document.documentElement.lang || 'en').split('-')[0] === 'fr' ? 'fr-FR' : 'en-US';
    }

    // `Intl.NumberFormat` with `style: 'currency'` handles BOTH the digit
    // grouping (`,` in en-US, narrow no-break space in fr-FR) AND the
    // symbol placement (left in en-US: `€1,228` / right with no-break
    // space in fr-FR: `1 228 €`) — neither was correct with the old
    // `sym() + toLocaleString()` approach.
    //
    // `maximumFractionDigits: 0` strips trailing `.00` for round amounts —
    // shipping rates are always whole-unit on Oblist (€1,228, not €1,228.00).
    // Falls back to the legacy `sym() + n.toLocaleString` if Intl isn't
    // available or rejects the currency code (oldest Safari, exotic ISOs).
    function formatPrice(amount) {
      var n = Math.round(Number(amount) || 0);
      try {
        return new Intl.NumberFormat(getLocale(), {
          style: 'currency',
          currency: currency,
          maximumFractionDigits: 0,
          minimumFractionDigits: 0
        }).format(n);
      } catch (_e) {
        return sym() + n.toLocaleString(getLocale());
      }
    }

    // ─── Arrival date formatting ──────────────────────────────────────────
    // Source of truth: the API now computes the full arrival window
    // server-side (production weeks + carrier transit days) and returns
    // `arrives_from` / `arrives_to` as ISO dates per option. We just format.
    //
    // Legacy path: if the backend still returns `transit_days_min/max` only
    // (e.g. during the migration), fall back to the local calculation using
    // the data-min-weeks / data-max-weeks production lead from the metafields.
    function formatDateRange(isoFrom, isoTo) {
      var dFrom = new Date(isoFrom);
      var dTo   = new Date(isoTo);
      if (isNaN(dFrom.getTime()) || isNaN(dTo.getTime())) return '';
      var opts = { month: 'short', day: 'numeric' };
      return dFrom.toLocaleDateString(getLocale(), opts) + ' – ' + dTo.toLocaleDateString(getLocale(), opts);
    }

    function formatArrivalWindow(transitMin, transitMax) {
      var today = new Date();
      var minDays = (minWeeks * 7) + (Number(transitMin) || 0);
      var maxDays = (maxWeeks * 7) + (Number(transitMax) || 0);
      var dMin = new Date(today.getTime() + minDays * 86400000);
      var dMax = new Date(today.getTime() + maxDays * 86400000);
      var opts = { month: 'short', day: 'numeric' };
      return dMin.toLocaleDateString(getLocale(), opts) + ' – ' + dMax.toLocaleDateString(getLocale(), opts);
    }

    function formatArrival(opt) {
      if (opt && opt.arrives_from && opt.arrives_to) {
        var server = formatDateRange(opt.arrives_from, opt.arrives_to);
        if (server) return server;
      }
      return formatArrivalWindow(opt && opt.transit_days_min, opt && opt.transit_days_max);
    }

    // ─── Compatibility filter (DEPRECATED — kept for reference only) ──────
    // Previously this hid "Standard shipping" parcel options for heavy /
    // oversized / high-value pieces. With the new API contract, the backend
    // is the single source of truth for which options to surface and in
    // which order — and `data.from_price` is authoritative for the lead
    // price. We no longer filter client-side. Trying to do so caused a bug
    // where `provider: "standard"` (sent by backend on "Direct delivery"
    // for compat) wrongly hid the cheapest option on heavy items, leaving
    // only the most expensive one as lead.
    //
    // This function is unused. Left in place in case we ever need to
    // reintroduce a defensive filter — but the default is to trust the API.
    function filterOptions(data) {
      return data.options || [];
    }

    // ─── Loading rotation (4 cosmetic phases, ~1.5s each) ─────────────────
    function startLoadingRotation() {
      if (!loadingText) return;
      var phases = [
        t('phase_location', 'Detecting your location…'),
        t('phase_specs',    'Reading product specs…'),
        t('phase_carriers', 'Querying carriers…'),
        t('phase_handlers', 'Querying art handlers…')
      ];
      var idx = 0;
      loadingText.textContent = phases[0];
      stopLoadingRotation();
      loadingInterval = setInterval(function () {
        idx = (idx + 1) % phases.length;
        loadingText.style.opacity = '0';
        setTimeout(function () {
          loadingText.textContent = phases[idx];
          loadingText.style.opacity = '1';
        }, 150);
      }, 1500);
    }

    function stopLoadingRotation() {
      if (loadingInterval) { clearInterval(loadingInterval); loadingInterval = null; }
    }

    // ─── State helpers ────────────────────────────────────────────────────
    function showLoading() {
      if (resultDiv) { resultDiv.style.display = 'none'; resultDiv.innerHTML = ''; }
      if (loadingDiv) loadingDiv.style.display = 'flex';
      startLoadingRotation();
    }

    function hideLoading() {
      if (loadingDiv) loadingDiv.style.display = 'none';
      stopLoadingRotation();
    }

    function showNote(message) {
      hideLoading();
      if (!resultDiv) return;
      resultDiv.innerHTML = '<p class="shipping-estimator__note">' + escapeHtml(message) + '</p>';
      resultDiv.style.display = 'block';
    }

    // ─── Rich fallback (no quote returned by the API) ─────────────────────
    // Even when the API returns no price, give the buyer something useful:
    //   1. Production lead time (from metafields — we already have this)
    //   2. A clear reassurance about the shipping/billing process
    //   3. A direct CTA to the curator for white-glove handling
    // Tone: curatorial, considerate. Not "sorry no price" — luxury delivery
    // is bespoke by nature, this is the moment to surface that.
    function renderRichFallback(data) {
      hideLoading();
      if (!resultDiv) return;

      var blocks = [];

      // 0. Header row — combines destination and lead time on a single line:
      //      "Shipping to United States · 6–8 weeks of atelier production"
      //    The location half is a click target → opens the modal Refine UI
      //    so the buyer can type a postal and try again.
      var fallbackDest = (data && data.destination) || {};
      var fallbackDestName = fallbackDest.country_name ||
        countryName(fallbackDest.country) ||
        countryName(marketsCountry) || '';

      // Lead time — focus on the NUMBERS, not the framing.
      // Page-level trust signals (rendered outside the widget) already say
      // "Made to order — crafted specifically for you" / "Delivery coordinated
      // by our team". Repeating "Crafted to order —" here would be pure
      // redundancy. We just give the actionable specifics: production weeks,
      // or ready-to-ship status for in-stock / vintage pieces.
      var leadText = '';
      if (isVintage || !isMadeToOrder) {
        leadText = t('ready_to_ship_short', 'In stock · ready to ship');
      } else if (minWeeks > 0 || maxWeeks > 0) {
        var range, isPlural;
        if (minWeeks && maxWeeks && minWeeks !== maxWeeks) {
          range = minWeeks + '–' + maxWeeks;
          isPlural = true;
        } else {
          var single = maxWeeks || minWeeks;
          range = String(single);
          isPlural = single !== 1;
        }
        var weeksLabel = isPlural
          ? t('weeks_atelier', 'weeks of atelier production')
          : t('week_atelier', 'week of atelier production');
        leadText = range + ' ' + weeksLabel;
      }
      // else: MTO without lead time set → skip the leadtime entirely

      var headerParts = [];
      if (fallbackDestName) {
        headerParts.push(
          '<button type="button" class="shipping-estimator__location-row" data-action="open-modal">' +
            '<span class="shipping-estimator__location-prefix">' + escapeHtml(t('shipping_to', 'Shipping to')) + '</span> ' +
            '<span class="shipping-estimator__location-text">' + escapeHtml(fallbackDestName) + '</span>' +
          '</button>'
        );
      }
      if (leadText) {
        headerParts.push(
          '<span class="shipping-estimator__fallback-leadtime">' + escapeHtml(leadText) + '</span>'
        );
      }
      if (headerParts.length) {
        blocks.push(
          '<div class="shipping-estimator__fallback-headline">' +
            headerParts.join(' <span class="shipping-estimator__fallback-sep">·</span> ') +
          '</div>'
        );
      }

      // 2. Reassurance note — process transparency without scaring the buyer.
      blocks.push('<p class="shipping-estimator__fallback-note">' +
        escapeHtml(t('fallback_note', 'Final shipping confirmed within 24h of your order — payable only when you accept it. International deliveries may include customs duties at the door.')) +
      '</p>');

      // 3. Curator CTA — clear escalation path for buyers who want certainty.
      blocks.push('<div class="shipping-estimator__fallback-curator">' +
        '<span class="shipping-estimator__fallback-curator-q">' +
          escapeHtml(t('curator_question', 'Need a precise quote?')) +
        '</span>' +
        '<button type="button" class="shipping-estimator__curator-btn" data-action="open-curator">' +
          escapeHtml(t('curator_cta', 'Ask a curator →')) +
        '</button>' +
      '</div>');

      resultDiv.innerHTML = blocks.join('');
      resultDiv.style.display = 'block';

      // Build a fallback modal so the location row above can open it.
      // The modal will have NO options (filtered=[]) but the standard
      // Refine row, banner, commitments and footer — enough for the buyer
      // to retry with a different destination.
      buildModal(data || {}, [], fallbackDestName);

      // Wire the curator CTA — same Intercom flow as the modal version
      var curatorBtn = resultDiv.querySelector('[data-action="open-curator"]');
      if (curatorBtn) {
        curatorBtn.addEventListener('click', function () { openCurator(data || {}); });
      }

      // Wire the location row → opens the modal with Refine UI. The
      // closure forwards the click target as the trigger so focus can be
      // returned to it on modal close (a11y).
      var openers = resultDiv.querySelectorAll('[data-action="open-modal"]');
      for (var oi = 0; oi < openers.length; oi++) {
        openers[oi].addEventListener('click', function (e) { openModal(e.currentTarget); });
      }
    }

    // ─── Inline location formatter ────────────────────────────────────────
    // Always surface whatever city/postal the API used for the estimate —
    // even when geo_level === 'country_only' and the postal is a fallback
    // default (e.g. country capital). UX rationale: hiding the postal leaves
    // the buyer wondering what assumption produced the price, and removes
    // the visual cue that they can refine it. By showing the postal as an
    // underlined click target, the buyer instantly understands "this is the
    // assumed destination — I can change it" and lands directly on the
    // Refine modal.
    function inlineLocation(dest /* geoLevel kept in signature for parity */) {
      var name = dest.country_name || countryName(dest.country);
      if (dest.city && dest.postal_code) return dest.city + ', ' + dest.postal_code;
      if (dest.postal_code)              return name + ' (' + dest.postal_code + ')';
      if (dest.city)                     return dest.city + ', ' + name;
      return name;
    }

    // ─── Range mode helpers ───────────────────────────────────────────────
    // The backend can return display_mode === 'range' when it judges that
    // showing granular options would mislead more than help (typically when
    // shipping is in the 30-50% ratio band of product price). Instead it
    // returns a min/max bracket and we display it as a single "estimated
    // shipping" callout with no carrier-specific options to expand into.
    //
    // Arrival window in range mode: backend doesn't pick a specific carrier,
    // so we estimate broadly using Concierge transit defaults (10-21 days)
    // on top of the production weeks. This matches the conservative end
    // (worst case) since range mode is usually triggered for delicate /
    // high-value pieces that lean toward white-glove handling.
    function rangeArrivalWindow(data) {
      var prodMin = (data.production && Number(data.production.weeks_min)) || minWeeks || 0;
      var prodMax = (data.production && Number(data.production.weeks_max)) || maxWeeks || 0;
      var today = new Date();
      var fromDate = new Date(today.getTime() + (prodMin * 7 + 10) * 86400000);
      var toDate   = new Date(today.getTime() + (prodMax * 7 + 21) * 86400000);
      return formatDateRange(fromDate.toISOString(), toDate.toISOString());
    }

    function rangeAmount(data) {
      var range = data.range || {};
      var min = Number(range.min) || 0;
      var max = Number(range.max) || 0;
      // Edge: if min === max, show a single price instead of "X – X"
      if (min && max && min === max) return formatPrice(min);
      return formatPrice(min) + ' – ' + formatPrice(max);
    }

    // Modal body block for range mode — replaces the options list
    function buildRangeBlock(data) {
      return (
        '<div class="shipping-estimator__range">' +
          '<div class="shipping-estimator__range-label">' +
            escapeHtml(t('range_label', 'Estimated shipping')) +
          '</div>' +
          '<div class="shipping-estimator__range-amount">' + escapeHtml(rangeAmount(data)) + '</div>' +
          '<div class="shipping-estimator__range-arrives">' +
            escapeHtml(t('arrives', 'Arrives')) + ' ' + escapeHtml(rangeArrivalWindow(data)) +
          '</div>' +
        '</div>'
      );
    }

    // Inline display (above the modal) for range mode
    function renderRangeResult(data) {
      hideLoading();

      var dest      = data.destination || {};
      var geoLevel  = data.geo_level || 'country_only';
      var location  = inlineLocation(dest, geoLevel);
      var arrival   = rangeArrivalWindow(data);
      var amount    = rangeAmount(data);
      var ctaLabel  = data.show_refine
        ? t('refine', 'Refine ✎')
        : t('see_options', 'See options');
      var dduRelevant = isDduRelevant(data);
      var inlineNote  = dduRelevant
        ? t('ddu_inline', 'Delivery DDU. Final shipping invoiced separately.')
        : t('inline_indicative', 'Final shipping invoiced separately.');

      if (resultDiv) {
        resultDiv.innerHTML =
          '<div class="shipping-estimator__lead">' +
            '<div>' +
              '<span class="shipping-estimator__lead-price">' + escapeHtml(amount) + '</span>' +
              '<span class="shipping-estimator__lead-arrival"> · ' +
                escapeHtml(t('arrives', 'Arrives')) + ' ' + escapeHtml(arrival) +
              '</span>' +
            '</div>' +
            '<button type="button" class="shipping-estimator__see-options-btn" data-action="open-modal">' +
              escapeHtml(ctaLabel) +
            '</button>' +
          '</div>' +
          '<button type="button" class="shipping-estimator__location-row" data-action="open-modal">' +
            '<span class="shipping-estimator__location-prefix">' + escapeHtml(t('shipping_to', 'Shipping to')) + '</span> ' +
            '<span class="shipping-estimator__location-text">' + escapeHtml(location) + '</span>' +
          '</button>' +
          '<div class="shipping-estimator__ddu">' + escapeHtml(inlineNote) + '</div>';
        resultDiv.style.display = 'block';
      }

      buildModal(data, [], location); // empty filtered array — modal uses range branch

      if (resultDiv) {
        var openers = resultDiv.querySelectorAll('[data-action="open-modal"]');
        for (var oi = 0; oi < openers.length; oi++) {
          openers[oi].addEventListener('click', function (e) { openModal(e.currentTarget); });
        }
      }
    }

    // ─── Render result ────────────────────────────────────────────────────
    // Strictly mutually-exclusive routing (early return on each branch) so
    // we never end up rendering, say, options AND a curator-only block, or
    // a range bracket AND options together.
    //
    //   Branch order:
    //     1. !success                → rich fallback (no quote)
    //     2. curator_only / 'curator' → rich fallback (inline) +
    //                                   modal renders curator-only block
    //     3. display_mode === 'range' → range result (inline + modal)
    //     4. !from_price              → rich fallback (no quote)
    //     5. options.length === 0     → rich fallback (nothing to show)
    //     6. default                  → standard inline + modal options
    function renderResult(data) {
      lastData = data;

      // Fire the GA4 exposure event BEFORE routing to sub-renderers. This
      // covers all success branches (price / range / curator_only) with a
      // single call and uses the internal `exposureFired` guard to dedupe
      // across re-renders (e.g. when the buyer refines country / postal
      // inside the modal and we re-render with fresh data).
      fireExposureEvent(data);

      if (!data || !data.success) {
        renderRichFallback(data);
        return;
      }

      // Curator-only mode — no inline price, just trust + lead time + curator
      // CTA (rich fallback handles this UX). The modal will render the
      // dedicated curator-only block thanks to buildModal's branch routing.
      if (data.curator_only || data.display_mode === 'curator') {
        renderRichFallback(data);
        return;
      }

      // Range mode — backend returns a min/max bracket instead of granular options.
      if (data.display_mode === 'range' && data.range) {
        renderRangeResult(data);
        return;
      }

      // No price → rich fallback
      if (!data.from_price) {
        renderRichFallback(data);
        return;
      }

      // Source of truth = data.from_price (authoritative cheapest computed
      // server-side). We never iterate options to find the lead price —
      // doing so previously caused us to display the wrong option (the
      // most expensive) when client-side filtering hid the cheapest one.
      var allOptions = data.options || [];
      if (allOptions.length === 0) {
        renderRichFallback(data);
        return;
      }

      // Find the option that matches from_price (for its arrival window).
      // Float comparison with epsilon tolerance to handle currency conversion
      // rounding. Falls back to options[0] (backend's preferred order).
      var leadOption = null;
      for (var li = 0; li < allOptions.length; li++) {
        if (Math.abs(Number(allOptions[li].price) - Number(data.from_price)) < 0.01) {
          leadOption = allOptions[li];
          break;
        }
      }
      if (!leadOption) leadOption = allOptions[0];

      var dest      = data.destination || {};
      var geoLevel  = data.geo_level || 'country_only';
      var location  = inlineLocation(dest, geoLevel);
      var arrivalLead = formatArrival(leadOption);

      // Single CTA — label depends on whether refinement is offered
      var ctaLabel = data.show_refine
        ? t('refine', 'Refine ✎')
        : t('see_options', 'See options');

      // Inline note: short DDU/indicative reminder under the lead price.
      // Uses local i18n only — the API's notes.indicative is now reserved
      // for the modal banner (luxe storytelling, not a one-liner).
      var dduRelevant = isDduRelevant(data);
      var inlineNote = dduRelevant
        ? t('ddu_inline', 'Delivery DDU. Final shipping invoiced separately.')
        : t('inline_indicative', 'Final shipping invoiced separately.');

      hideLoading();

      if (resultDiv) {
        // Compact 4-row layout:
        //   1. (rendered in Liquid) vendor · origin · in-stock meta
        //   2. (here) lead price + arrival + Refine/See-options CTA
        //   3. (here) "Shipping to X" — click target opening the modal
        //   4. (here) DDU disclaimer with a thin top divider — clearly
        //      separated from the lead block so it reads as fine print.
        //
        // The location row is itself a click target — clicking it opens the
        // modal (same action as the CTA button). Buyers expect both surfaces
        // to take them to the refinement screen.
        //
        // Display price = data.from_price (authoritative), NOT leadOption.price
        // (we used to do that and got bitten by client-side filtering hiding
        // the cheapest option).
        resultDiv.innerHTML =
          '<div class="shipping-estimator__lead">' +
            '<div>' +
              '<span class="shipping-estimator__lead-price">' +
                '<span class="shipping-estimator__lead-price-from">' + escapeHtml(t('from', 'From')) + '</span> ' +
                escapeHtml(formatPrice(data.from_price)) +
              '</span>' +
              '<span class="shipping-estimator__lead-arrival"> · ' +
                escapeHtml(t('arrives', 'Arrives')) + ' ' + escapeHtml(arrivalLead) +
              '</span>' +
            '</div>' +
            '<button type="button" class="shipping-estimator__see-options-btn" data-action="open-modal">' +
              escapeHtml(ctaLabel) +
            '</button>' +
          '</div>' +
          '<button type="button" class="shipping-estimator__location-row" data-action="open-modal">' +
            '<span class="shipping-estimator__location-prefix">' + escapeHtml(t('shipping_to', 'Shipping to')) + '</span> ' +
            '<span class="shipping-estimator__location-text">' + escapeHtml(location) + '</span>' +
          '</button>' +
          '<div class="shipping-estimator__ddu">' + escapeHtml(inlineNote) + '</div>';
        resultDiv.style.display = 'block';
      }

      buildModal(data, allOptions, location);

      // Wire ALL elements with data-action="open-modal" (the CTA button AND
      // the location row are both click targets). Each click forwards its
      // own `currentTarget` so focus can be returned to the exact trigger
      // on modal close (a11y) — e.g. focus comes back to the location row
      // if the buyer opened the modal from there, not from the CTA button.
      if (resultDiv) {
        var openers = resultDiv.querySelectorAll('[data-action="open-modal"]');
        for (var oi = 0; oi < openers.length; oi++) {
          openers[oi].addEventListener('click', function (e) { openModal(e.currentTarget); });
        }
      }
    }

    // ─── Banner (top of modal body) — unified reassurance block.
    //
    // Combines the two related "trust the 24h confirmation" messages so
    // they live in the same visual box (visual unity per buyer feedback):
    //   1. notes.payment (or fallback) — "Shipping not charged at checkout,
    //      curator confirms within 24h" — process clarity.
    //   2. notes.ratio_message — "Our team typically negotiates better rates
    //      for pieces of this caliber" — value reassurance.
    //
    // BUT: when in curator_only mode, ratio_message is the MAIN message of
    // the curator-only body block. Rendering it here too would duplicate
    // the same text in two places of the same modal. So we skip the
    // ratio_message in the banner when curator_only is active — the
    // curator-only block owns it exclusively in that mode.
    //
    // The poetic headline lives in the modal HEADER, not here.
    function buildBanner(data) {
      var n = (data && data.notes) || {};
      var bannerText = n.payment || n.indicative ||
        t('modal_intro', 'These rates are indicative. The vendor may have access to cheaper carriers and can sometimes secure a better price — the final shipping cost is confirmed within 24h after your order. This gives you a ballpark idea before purchase.');

      var isCuratorMode = !!(data && (data.curator_only || data.display_mode === 'curator'));

      var html = '<div class="shipping-estimator__banner">';
      html += '<p class="shipping-estimator__banner-payment">' + escapeHtml(bannerText) + '</p>';
      if (n.ratio_message && !isCuratorMode) {
        html += '<p class="shipping-estimator__banner-ratio">' + escapeHtml(n.ratio_message) + '</p>';
      }
      html += '</div>';
      return html;
    }

    // ─── Contextual notes (above the options list) ───────────────────────
    // Renders backend-provided `notes.cross_continent_fragile` and
    // `notes.mto` as small italic callouts when present. Color-coded so the
    // buyer scans them as "here's something important about THIS shipment".
    //
    // Defensive guard on the MTO note: the backend may send it generically,
    // but we only show it when this specific product is actually made-to-order
    // (mtoproduct.mtop == "Yes" AND not tagged vintage). Without this guard,
    // a vintage one-of-a-kind piece would wrongly display "Your piece is being
    // crafted now…" — which contradicts the very nature of vintage.
    function buildContextNotes(n) {
      var html = '';
      if (n.cross_continent_fragile) {
        html += '<p class="shipping-estimator__context-note shipping-estimator__context-note--fragile">' +
          escapeHtml(n.cross_continent_fragile) +
        '</p>';
      }
      if (n.mto && isMadeToOrder && !isVintage) {
        html += '<p class="shipping-estimator__context-note shipping-estimator__context-note--mto">' +
          escapeHtml(n.mto) +
        '</p>';
      }
      return html;
    }

    // ─── Below-options notes (historical context only) ────────────────────
    // notes.ratio_message has a single render site:
    //   • Standard / range modes → in the banner (alongside notes.payment),
    //     visually unified per buyer feedback "ne separe pas les blocs".
    //   • Curator-only mode → as the main message of the curator-only block.
    // It never appears twice — banner skips it in curator mode.
    //
    // notes.historical_context ("Based on N past deliveries to {country},
    // this corridor typically falls between X-Y€") is a distinct angle —
    // historical evidence, complementary to ratio_message's reassurance.
    // It stays below the options where it fits the "evidence after seeing
    // the prices" reading order.
    function buildBelowOptionsNotes(n) {
      if (!n || !n.historical_context) return '';
      return '<p class="shipping-estimator__historical">' +
        escapeHtml(n.historical_context) +
      '</p>';
    }

    // ─── Curator-only mode (high shipping ratio, no price shown) ─────────
    // When `data.curator_only === true`, the API decided that surfacing a
    // numeric estimate would do more harm than good (e.g. shipping > 50% of
    // product price). We replace the options entirely with a soft message
    // and a single, prominent curator CTA.
    function buildCuratorOnlyBlock(data) {
      var msg = (data.notes && data.notes.ratio_message) ||
        t('curator_only_default', 'For this piece, our curator will arrange a bespoke shipping quote tailored to your destination.');
      return (
        '<div class="shipping-estimator__curator-only">' +
          '<p class="shipping-estimator__curator-only-msg">' + escapeHtml(msg) + '</p>' +
          '<button type="button" class="shipping-estimator__curator-only-cta" data-action="open-curator">' +
            escapeHtml(t('speak_curator_cta', 'Speak with your curator →')) +
          '</button>' +
        '</div>'
      );
    }

    // ─── Commitments — The Oblist promises (always shown when API sends them)
    // Reinforces the brand's delivery guarantees as the last thing the buyer
    // reads before deciding. The backend ships a generic list (damage
    // guarantee + concierge dispatch + returns); the frontend overrides
    // the returns item with the policy that matches the rest of the theme
    // (see snippets/return-policy.liquid and `responsability_buyers` in
    // locales) — keeping the messaging consistent across surfaces:
    //
    //   • Returnable pieces      → "Return accepted within 14 days —
    //                              buyer covers return shipping."
    //                              (NOT "we reimburse shipping in full",
    //                              which contradicts responsability_buyers)
    //
    //   • Non-returnable pieces  → "Final sale — Buyer Protection
    //                              coverage for damage or non-delivery."
    //                              (driven by arforproduct.arfp == 'No',
    //                              keeps Final Sale messaging aligned with
    //                              the product-page return policy modal)
    //
    // Skipped silently if the API doesn't send commitments.
    function buildCommitmentsBlock(data) {
      var commits = (data && data.commitments) || [];
      if (!commits.length) return '';

      // Always drop the backend's returns/refund commitment — we replace
      // it with a frontend-controlled one whose copy matches the theme's
      // policy elsewhere. Defensive content match (multilingual keywords)
      // rather than positional, so backend reorderings don't break this.
      commits = commits.filter(function (c) {
        var text = ((c.title || '') + ' ' + (c.detail || '')).toLowerCase();
        return !/(return|retour|refund|rembours|exchange|échange|echange)/.test(text);
      });

      // Append the right replacement based on the product's return policy.
      if (isReturnable) {
        commits.push({
          title: t('commitment_returns_title', 'Return accepted within 14 days'),
          detail: t('commitment_returns_detail', 'We coordinate the pickup at your address. Return shipping is at the buyer\u2019s expense.')
        });
      } else {
        commits.push({
          title: t('commitment_final_sale_title', 'Final sale'),
          detail: t('commitment_final_sale_detail', 'This piece is non-refundable, but damage or non-delivery remain covered by The Oblist Buyer Protection.')
        });
      }

      var items = commits.map(function (c) {
        return '<li class="shipping-estimator__commitment">' +
          '<strong class="shipping-estimator__commitment-title">' + escapeHtml(c.title || '') + '</strong>' +
          (c.detail ? '<span class="shipping-estimator__commitment-detail">' + escapeHtml(c.detail) + '</span>' : '') +
        '</li>';
      }).join('');
      return (
        '<div class="shipping-estimator__commitments">' +
          '<h4 class="shipping-estimator__commitments-title">' +
            escapeHtml(t('our_promise', 'Our promise')) +
          '</h4>' +
          '<ul class="shipping-estimator__commitments-list">' + items + '</ul>' +
        '</div>'
      );
    }

    // ─── Footer (curator CTA + multilingual availability) ────────────────
    function buildFooterBlock() {
      return (
        '<div class="shipping-estimator__modal-footer">' +
          '<div class="shipping-estimator__modal-footer-curator">' +
            '<span class="shipping-estimator__modal-footer-q">' +
              escapeHtml(t('curator_question_footer', 'Need to discuss your shipping?')) +
            '</span>' +
            '<button type="button" class="shipping-estimator__modal-footer-cta" data-action="open-curator">' +
              escapeHtml(t('curator_cta', 'Ask a curator →')) +
            '</button>' +
          '</div>' +
          '<p class="shipping-estimator__modal-footer-availability">' +
            escapeHtml(t('availability', 'Available in French, English & German')) +
          '</p>' +
        '</div>'
      );
    }

    // ─── Modal popup ──────────────────────────────────────────────────────
    // Replaces any existing modal. Preserves the "is-visible" state if the
    // previous modal was open (so the Refine flow updates in-place).
    function buildModal(data, filtered, location) {
      var prevBackdrop = document.getElementById('shipping-estimator-modal-backdrop');
      var prevModal    = document.getElementById('shipping-estimator-modal');
      var wasOpen = !!(prevModal && prevModal.classList.contains('is-visible'));

      if (prevBackdrop) prevBackdrop.remove();
      if (prevModal)    prevModal.remove();

      var dest = data.destination || {};
      var geoLevelModal = data.geo_level || 'country_only';

      // Refine section — ALWAYS shown so the buyer (a) sees the country and
      // postal currently used to compute these prices, and (b) can change
      // either or both (e.g. ship to parents abroad).
      //
      // Pre-fill priority for the postal input:
      //   1. API destination.postal_code (the postal the prices are based on)
      //   2. customer.default_address.zip (passed via data-prefill-postal)
      var startPostal = dest.postal_code || prefillPostal || '';
      var startCountry = dest.country || '';
      var refineLabel = (geoLevelModal === 'full' && dest.postal_code)
        ? t('refine_label_confirmed', 'Shipping to this postal code — update to ship elsewhere')
        : t('refine_label', 'Enter your postal code for a precise estimate');

      // Country options: from window.SHIPPING_ESTIMATOR_COUNTRIES (Markets-driven).
      // Shopify Markets returns countries in its own configured order (often
      // by market priority, not alphabetical), which makes scanning a long
      // list painful for the buyer. We sort alphabetically by localized name
      // so the dropdown is predictable — "Australia" before "Belgium" before
      // "Canada" regardless of Markets configuration.
      //
      // If the current destination country isn't in the list (rare — e.g. a
      // userDest from an older Markets config), we prepend it so the buyer
      // still sees it as the active selection without losing context.
      var countriesList = (window.SHIPPING_ESTIMATOR_COUNTRIES || []).slice().sort(function (a, b) {
        return (a.name || '').localeCompare(b.name || '', getLocale());
      });
      var currentInList = false;
      var optionsHtmlBuilder = '';
      for (var ci = 0; ci < countriesList.length; ci++) {
        var c = countriesList[ci];
        if (c.iso === startCountry) currentInList = true;
        optionsHtmlBuilder += '<option value="' + escapeHtml(c.iso) + '"' +
          (c.iso === startCountry ? ' selected' : '') + '>' +
          escapeHtml(c.name) + '</option>';
      }
      if (startCountry && !currentInList) {
        optionsHtmlBuilder = '<option value="' + escapeHtml(startCountry) + '" selected>' +
          escapeHtml(countryName(startCountry)) + '</option>' + optionsHtmlBuilder;
      }

      var refineSection =
        '<div class="shipping-estimator__refine">' +
          '<label class="shipping-estimator__refine-label">' +
            escapeHtml(refineLabel) +
          '</label>' +
          '<div class="shipping-estimator__refine-row">' +
            '<select class="shipping-estimator__refine-country" aria-label="' + escapeHtml(t('country_placeholder', 'Country')) + '">' +
              optionsHtmlBuilder +
            '</select>' +
            '<input type="text" class="shipping-estimator__refine-input" inputmode="numeric" autocomplete="postal-code"' +
              ' value="' + escapeHtml(startPostal) + '"' +
              ' placeholder="' + escapeHtml(t('postal_placeholder', 'Postal / Zip code')) + '"' +
              ' aria-label="' + escapeHtml(t('postal_placeholder', 'Postal / Zip code')) + '" />' +
            '<button type="button" class="shipping-estimator__refine-button">' +
              escapeHtml(t('refine_button', 'Update estimate')) +
            '</button>' +
          '</div>' +
        '</div>';

      // Options — uses the rich content from the API (description, caveat,
      // best_for, services). Falls back to local i18n descriptions if the
      // API doesn't provide a description (legacy shape).
      //
      // Recommended badge: first option in the filtered list, but ONLY if it
      // has no caveat (a "recommended" badge next to a "⚠ not suitable" caveat
      // would be confusing — the caveat is louder than the badge).
      var optionsHtml = filtered.map(function (opt, i) {
        var description = opt.description || t(descKey(opt.type), '');
        var caveat      = opt.caveat || '';
        var explainer   = opt.price_explainer || '';
        var bestFor     = opt.best_for || '';
        var services    = opt.services || [];
        var arrival = formatArrival(opt);
        var isRecommended = (i === 0) && !caveat;
        var recBadge = isRecommended
          ? ' <span class="shipping-estimator__rec">● ' + escapeHtml(t('recommended', 'recommended')) + '</span>'
          : '';

        // "What's included" expandable block — only render if we actually have
        // content (services list and/or best_for).
        var detailsHtml = '';
        if (services.length || bestFor) {
          var servicesHtml = services.length
            ? '<ul class="shipping-estimator__option-services">' +
                services.map(function (s) {
                  return '<li>' + escapeHtml(s) + '</li>';
                }).join('') +
              '</ul>'
            : '';
          var bestForHtml = bestFor
            ? '<p class="shipping-estimator__option-bestfor"><strong>' +
                escapeHtml(t('best_for', 'Best for:')) + '</strong> ' + escapeHtml(bestFor) +
              '</p>'
            : '';
          detailsHtml =
            '<details class="shipping-estimator__option-details">' +
              '<summary class="shipping-estimator__option-summary">' +
                escapeHtml(t('whats_included', "What's included")) +
              '</summary>' +
              servicesHtml +
              bestForHtml +
            '</details>';
        }

        return (
          '<div class="shipping-estimator__option' + (caveat ? ' has-caveat' : '') + '">' +
            '<div class="shipping-estimator__option-row">' +
              '<span class="shipping-estimator__option-type">' + escapeHtml(opt.type) + recBadge + '</span>' +
              '<span class="shipping-estimator__option-price">' +
                '<span class="shipping-estimator__option-price-from">' + escapeHtml(t('from', 'From')) + '</span> ' +
                escapeHtml(formatPrice(opt.price)) +
              '</span>' +
            '</div>' +
            '<div class="shipping-estimator__option-arrival">' +
              escapeHtml(t('arrives', 'Arrives')) + ' ' + escapeHtml(arrival) +
            '</div>' +
            (description ? '<div class="shipping-estimator__option-desc">' + escapeHtml(description) + '</div>' : '') +
            (explainer ? '<p class="shipping-estimator__option-explainer">' + escapeHtml(explainer) + '</p>' : '') +
            (caveat ? '<p class="shipping-estimator__option-caveat" role="note">⚠ ' + escapeHtml(caveat) + '</p>' : '') +
            detailsHtml +
          '</div>'
        );
      }).join('');

      // ─── Header ─────────────────────────────────────────────────────────
      // Title now uses the API-provided poetic headline ("Your delivery,
      // considered.") rendered in a serif typeface. Falls back to the local
      // i18n "Shipping cost estimation" when the API doesn't send one.
      var n = data.notes || {};
      var headlineText = n.headline || t('modal_title', 'Shipping cost estimation');
      var titleCountry = dest.country_name || countryName(dest.country);

      // Subtitle composition — show the MOST SPECIFIC info we have, drop
      // redundancy. If we know the city, we don't need to repeat the country
      // (Paris IS in France — the line "Shipping to France · Paris, 75000"
      // is pleonastic and wastes header space, pushing the sticky header
      // taller and shrinking the scrollable body area).
      //
      //   • city + postal             → "Paris, 75000"
      //   • city only                 → "Paris"
      //   • postal only               → "75000, France"
      //   • country only (no postal)  → "France"
      var destLine = '';
      if (dest.city && dest.postal_code) {
        destLine = dest.city + ', ' + dest.postal_code;
      } else if (dest.city) {
        destLine = dest.city;
      } else if (dest.postal_code && titleCountry) {
        destLine = dest.postal_code + ', ' + titleCountry;
      } else if (dest.postal_code) {
        destLine = dest.postal_code;
      } else if (titleCountry) {
        destLine = titleCountry;
      }

      // ─── Body content ──────────────────────────────────────────────────
      // Four routes depending on the display_mode signal from the API:
      //   • 'curator' (or legacy curator_only:true) → replace options entirely
      //     with a curator CTA (high shipping ratio, no price shown).
      //   • 'range' → show a min-max bracket as a single callout, no granular
      //     options. Used when backend judges options would mislead more
      //     than help (mid-range shipping ratio, delicate items).
      //   • standard with options → context notes + options list + below-notes.
      //   • fallback (no options at all) → only the Refine row + commitments
      //     + footer (no body content) so the buyer can retry with a postal.
      var bodyContent;
      if (data.curator_only || data.display_mode === 'curator') {
        bodyContent = buildCuratorOnlyBlock(data);
      } else if (data.display_mode === 'range' && data.range) {
        bodyContent = buildRangeBlock(data) + buildBelowOptionsNotes(n);
      } else if (filtered && filtered.length > 0) {
        bodyContent =
          buildContextNotes(n) +
          '<div class="shipping-estimator__options-list">' + optionsHtml + '</div>' +
          buildBelowOptionsNotes(n);
      } else {
        // Fallback mode — no options to display. The Refine row in the modal
        // is the buyer's escape hatch: type a postal and try again.
        bodyContent = '';
      }

      var modalHtml =
        '<div id="shipping-estimator-modal-backdrop" class="shipping-estimator__backdrop' + (wasOpen ? ' is-visible' : '') + '"></div>' +
        '<div id="shipping-estimator-modal" class="shipping-estimator__modal' + (wasOpen ? ' is-visible' : '') + '" role="dialog" aria-modal="true" aria-labelledby="shipping-estimator-modal-title">' +
          '<div class="shipping-estimator__modal-header">' +
            '<div class="shipping-estimator__modal-header-text">' +
              '<h3 id="shipping-estimator-modal-title" class="shipping-estimator__modal-title">' +
                escapeHtml(headlineText) +
              '</h3>' +
              (destLine ? '<div class="shipping-estimator__modal-sub">' +
                escapeHtml(t('modal_destination', 'Shipping to')) + ' ' + escapeHtml(destLine) +
              '</div>' : '') +
            '</div>' +
            '<button type="button" class="shipping-estimator__modal-close" aria-label="' + escapeHtml(t('close', 'Close')) + '">×</button>' +
          '</div>' +
          '<div class="shipping-estimator__modal-body">' +
            buildBanner(data) +
            refineSection +
            bodyContent +
            buildCommitmentsBlock(data) +
            buildFooterBlock() +
          '</div>' +
        '</div>';

      document.body.insertAdjacentHTML('beforeend', modalHtml);

      var closeBtn = document.querySelector('#shipping-estimator-modal .shipping-estimator__modal-close');
      var backdrop = document.getElementById('shipping-estimator-modal-backdrop');
      if (closeBtn) closeBtn.addEventListener('click', closeModal);
      if (backdrop) backdrop.addEventListener('click', closeModal);

      // Wire ALL "open-curator" CTAs — there can be several in the modal
      // (curator-only block, footer, etc.). Each one opens Intercom with
      // product + destination context.
      var curatorBtns = document.querySelectorAll('#shipping-estimator-modal [data-action="open-curator"]');
      for (var ci = 0; ci < curatorBtns.length; ci++) {
        curatorBtns[ci].addEventListener('click', function (e) {
          e.preventDefault();
          openCurator(data);
        });
      }

      // Wire the Refine button (reads BOTH country select and postal input)
      var refineBtn = document.querySelector('#shipping-estimator-modal .shipping-estimator__refine-button');
      var refineInput = document.querySelector('#shipping-estimator-modal .shipping-estimator__refine-input');
      var refineCountry = document.querySelector('#shipping-estimator-modal .shipping-estimator__refine-country');

      // When the buyer switches country, clear the postal input.
      // Without this, the previous postal (e.g. Spanish "28004") would stay
      // in the field for a different country (e.g. Poland) and the API would
      // reject the mismatch with a generic "Unable to estimate" error.
      //
      // We ALSO persist the new country immediately (without postal) so the
      // buyer's destination intent survives navigation to the next product.
      // Previously, persistence required a successful refine API call —
      // meaning country selections alone (without a typed postal) were lost
      // on every page change, forcing the buyer to re-pick on every PDP.
      if (refineCountry && refineInput) {
        refineCountry.addEventListener('change', function () {
          if (refineCountry.value !== (dest.country || '')) {
            refineInput.value = '';
            refineInput.focus();
            setUserDest(refineCountry.value, '');
          }
        });
      }

      if (refineBtn) {
        var triggerRefine = function () {
          var postal = refineInput ? refineInput.value.trim() : '';
          var country = refineCountry ? refineCountry.value : (dest.country || '');
          if (!country) return;
          if (!postal) { if (refineInput) refineInput.focus(); return; }
          handleRefine(country, postal);
        };
        refineBtn.addEventListener('click', triggerRefine);
        if (refineInput) {
          refineInput.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') { e.preventDefault(); triggerRefine(); }
          });
        }
      }
    }

    // ─── Curator CTA → open Intercom ──────────────────────────────────────
    // Uses the same proven pattern as the existing "Ask a Curator" button
    // (snippets/customer-support.liquid + product-info.liquid:1336):
    //
    //   document.getElementById("chat-with-us").addEventListener("click",
    //     function() { Intercom('show'); });
    //
    // We add an analytics event with product + destination context so the
    // curator team can distinguish shipping-related help requests in Intercom.
    function openCurator(data) {
      var dest = (data && data.destination) || {};

      // Track the source — no-op if Intercom hasn't fully loaded yet
      // (Intercom installs a queueing placeholder on page load, so even
      // pre-load calls don't error and are flushed when ready).
      try {
        if (typeof window.Intercom === 'function') {
          window.Intercom('trackEvent', 'shipping_estimate_help_requested', {
            product_id: productId,
            product_title: productTitle || null,
            destination_country: dest.country || null,
            destination_postal: dest.postal_code || null
          });
        }
      } catch (_e) { /* never block opening the chat over an analytics error */ }

      // Close our shipping modal so Intercom has the foreground (one focal
      // point at a time — better mobile UX in particular).
      closeModal();

      // Open Intercom — same call as the existing curator button.
      if (typeof window.Intercom === 'function') {
        window.Intercom('show');
        return;
      }

      // Last-resort fallback: navigate with ?open-chat=true so the helper
      // in layout/theme.liquid (openIntercomWhenReady) picks it up. Only
      // hits this branch if Intercom failed to install (ad-blocker etc.).
      var sep = window.location.search ? '&' : '?';
      window.location.href = window.location.pathname + window.location.search + sep + 'open-chat=true';
    }

    // ─── Modal a11y + scroll lock state ──────────────────────────────────
    // `lastTrigger`  : the element that opened the modal — we return focus
    //                  to it on close so keyboard users don't get lost.
    // `lockedScrollY`: the page scroll position at the moment we open. iOS
    //                  Safari ignores `body { overflow: hidden }`, so we
    //                  pin the body in place with `position: fixed; top:
    //                  -scrollY` instead — the standard workaround.
    var lastTrigger  = null;
    var lockedScrollY = 0;

    function escClose(e) { if (e.key === 'Escape') closeModal(); }

    // Focus trap — keeps Tab cycling inside the modal so keyboard users
    // can't accidentally tab out into the page underneath (which would be
    // confusing and an a11y violation for `role="dialog" aria-modal="true"`).
    // We re-scan focusables on every Tab because the modal content can
    // change (refine fetch swaps options, details toggles expand, etc.).
    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      var modal = document.getElementById('shipping-estimator-modal');
      if (!modal) return;
      var focusables = modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
        ' select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]),' +
        ' details > summary'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last  = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // Measure the sticky header's height after the modal is rendered, so
    // the sticky refine row can pin itself just below it. We expose the
    // measurement as a CSS variable on the modal element — the CSS picks
    // it up via `top: var(--shipping-estimator-header-height, 88px)`.
    // Done on every open in case the title wrapped differently (e.g. long
    // headline in French vs English).
    function syncHeaderHeight(modal) {
      var header = modal.querySelector('.shipping-estimator__modal-header');
      if (!header) return;
      var h = header.getBoundingClientRect().height;
      if (h > 0) modal.style.setProperty('--shipping-estimator-header-height', h + 'px');
    }

    function openModal(triggerEl) {
      var backdrop = document.getElementById('shipping-estimator-modal-backdrop');
      var modal    = document.getElementById('shipping-estimator-modal');
      if (!backdrop || !modal) return;

      // Detect if the body is already locked from a previous open — this
      // happens after a successful refine which rebuilds the modal DOM and
      // re-calls openModal(). We must NOT re-lock in that case, because
      // `window.scrollY` reads as 0 when the body is already `position:
      // fixed`, which would corrupt `lockedScrollY` and cause closeModal
      // to scroll to the top of the page instead of the original position.
      var alreadyLocked = document.body.style.position === 'fixed';

      // Remember who opened the modal — returned focus target on close.
      // Only set on the FIRST open of a session: internal re-opens (e.g.
      // after a successful refine that rebuilds the modal DOM) shouldn't
      // overwrite the original outside-page trigger with a now-destroyed
      // in-modal button reference.
      if (!lastTrigger) {
        lastTrigger = triggerEl || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      }

      backdrop.classList.add('is-visible');
      modal.classList.add('is-visible');

      // iOS-safe body scroll lock — pin the body in place at the current
      // scroll position. `overflow: hidden` alone doesn't work on iOS
      // Safari (the page keeps scrolling underneath). Restored on close.
      // Skipped if the body is already locked from a previous open call.
      if (!alreadyLocked) {
        lockedScrollY = window.scrollY || window.pageYOffset || 0;
        document.body.style.position = 'fixed';
        document.body.style.top      = '-' + lockedScrollY + 'px';
        document.body.style.left     = '0';
        document.body.style.right    = '0';
        document.body.style.width    = '100%';

        document.addEventListener('keydown', escClose);
        document.addEventListener('keydown', trapFocus);
      }

      syncHeaderHeight(modal);

      // Move focus inside the modal — close button is the safest first
      // target (always present, doesn't risk grabbing the OS soft keyboard
      // on mobile like a text input would). Only on the initial open: on
      // refine re-opens, the buyer's focus is probably on the postal input
      // or the refine button — stealing it back to the close X would be
      // disorienting (and would dismiss the mobile keyboard mid-flow).
      if (!alreadyLocked) {
        var firstFocus = modal.querySelector('.shipping-estimator__modal-close');
        if (firstFocus && typeof firstFocus.focus === 'function') {
          // Slight defer so the modal's opacity transition begins first —
          // focusing during a CSS transition can cause a visual jump.
          setTimeout(function () { firstFocus.focus(); }, 0);
        }
      }
    }

    function closeModal() {
      var backdrop = document.getElementById('shipping-estimator-modal-backdrop');
      var modal    = document.getElementById('shipping-estimator-modal');
      if (!backdrop || !modal) return;
      backdrop.classList.remove('is-visible');
      modal.classList.remove('is-visible');

      // Restore body scroll AND the page's scroll position. Setting all
      // properties to '' lets the stylesheet defaults win again.
      document.body.style.position = '';
      document.body.style.top      = '';
      document.body.style.left     = '';
      document.body.style.right    = '';
      document.body.style.width    = '';
      window.scrollTo(0, lockedScrollY);

      document.removeEventListener('keydown', escClose);
      document.removeEventListener('keydown', trapFocus);

      // Return focus to whatever opened the modal so keyboard / screen
      // reader users keep their place in the page.
      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        try { lastTrigger.focus(); } catch (_e) { /* element gone — no-op */ }
      }
      lastTrigger = null;
    }

    // ─── Stale modal content cleanup ──────────────────────────────────────
    // Removes everything tied to a previous successful estimate (options,
    // context notes, ratio message, historical context, range bracket,
    // curator-only block). Called BEFORE a refine fetch so a failed refine
    // can't end up showing the OLD postal's options alongside the NEW
    // postal's error message — which would mislead the buyer into thinking
    // those prices apply to the postal they just typed.
    function clearStaleModalContent() {
      var modal = document.getElementById('shipping-estimator-modal');
      if (!modal) return;
      var selectors = [
        '.shipping-estimator__options-list',
        '.shipping-estimator__context-note',
        '.shipping-estimator__ratio-msg',
        '.shipping-estimator__historical',
        '.shipping-estimator__curator-only',
        '.shipping-estimator__range'
      ];
      var stale = modal.querySelectorAll(selectors.join(', '));
      for (var i = 0; i < stale.length; i++) {
        if (stale[i].parentNode) stale[i].parentNode.removeChild(stale[i]);
      }
    }

    // ─── Refine handler (re-fetch with explicit country + postal) ─────────
    // Both arguments come from the modal's Refine row (country select + postal
    // input). The buyer can change either or both before clicking Update.
    //
    // UX flow:
    //   • clear stale content (options, notes from previous postal)
    //   • show loading state on the Refine button
    //   • on success → renderResult rebuilds the modal entirely with new data
    //   • on failure → error in Refine row, no stale options to mislead
    function handleRefine(country, postal) {
      if (!country || !postal) return;

      var refineBtn = document.querySelector('#shipping-estimator-modal .shipping-estimator__refine-button');
      var refineRow = document.querySelector('#shipping-estimator-modal .shipping-estimator__refine');
      var prevBtnText = refineBtn ? refineBtn.textContent : '';

      // Clear any previous error message from a past attempt
      var prevErr = refineRow && refineRow.querySelector('.shipping-estimator__refine-error');
      if (prevErr) prevErr.remove();

      // Wipe every element from the previous estimate. If the refine fails,
      // the buyer sees an error (not stale options + error coexisting).
      clearStaleModalContent();

      // Inline loading state on the button only
      if (refineBtn) {
        refineBtn.disabled = true;
        refineBtn.textContent = t('loading_button', 'Loading…');
      }

      function showRefineError(msg) {
        if (!refineRow) return;
        // Remove any pre-existing error before adding the new one
        var existing = refineRow.querySelector('.shipping-estimator__refine-error');
        if (existing) existing.remove();
        refineRow.insertAdjacentHTML('beforeend',
          '<p class="shipping-estimator__refine-error" role="alert">' + escapeHtml(msg) + '</p>'
        );
      }

      function restoreBtn() {
        if (refineBtn) {
          refineBtn.disabled = false;
          refineBtn.textContent = prevBtnText;
        }
      }

      var suffix = 'manual:' + country + ':' + postal + ':' + currency;
      var cached = getCached(productId, suffix);
      if (cached) {
        renderResult(cached);
        openModal();
        return;
      }

      fetchEstimate({ productId: productId, destCountry: country, destPostal: postal, currency: currency })
        .then(function (data) {
          // Three routes, in order of strictness:
          //
          //   1. data.success === true AND from_price → standard happy path.
          //      Cache, persist user destination, re-render and re-open.
          //
          //   2. data.success === true but no from_price → the API replied
          //      cleanly, just with no price. This is NOT an error — it's a
          //      spec-compliant fallback (e.g. `curator_only: true`, or no
          //      carrier could quote this destination but the curator team
          //      will arrange it). We must let renderResult route to the
          //      right fallback (curator-only block, rich fallback, range)
          //      instead of slapping a red error on the refine row. The red
          //      error is reserved for cases where the buyer CAN fix the
          //      input (typo'd postal, etc.) — telling them "no estimate
          //      found, check your postal" when the truth is "this piece
          //      ships via white-glove only, our curator will handle it"
          //      misleads them into endless postal-retyping.
          //
          //   3. data.success === false (or data falsy) → explicit failure.
          //      Show the red error so the buyer can retry / refine.
          //
          // We also persist the user destination in case 2 so the country
          // choice survives navigation (postal still saved since they typed
          // a valid-looking one, even if no carrier matched).
          if (data && data.success) {
            if (data.from_price) {
              setCached(productId, suffix, data);
            }
            setUserDest(country, postal);
            renderResult(data);
            openModal(); // the modal was rebuilt; re-open it (preserves visible state)
            return;
          }

          // No quote for this combo — most common cause: country/postal mismatch
          // (e.g. Spanish postal "28004" left in the field while country switched to Poland).
          showRefineError(t('refine_error_invalid', 'No estimate found for this country and postal code. Check your postal code and try again.'));
          restoreBtn();
        })
        .catch(function () {
          showRefineError(t('try_again', 'Unable to estimate shipping. Please try again.'));
          restoreBtn();
        });
    }

    // ─── Diagnostic logging (opt-in, prod-safe) ───────────────────────────
    // Enabled when the URL contains ?oblist-debug=1 OR window.OBLIST_DEBUG
    // is truthy. Logs every API response shape so we can spot widget /
    // backend mismatches in the wild without polluting normal users' consoles.
    function debugEnabled() {
      try {
        if (window.OBLIST_DEBUG) return true;
        return /[?&]oblist-debug=1\b/.test(window.location.search);
      } catch (_e) { return false; }
    }

    function debugLogResponse(label, params, data, durationMs) {
      if (!debugEnabled()) return;
      try {
        // eslint-disable-next-line no-console
        console.groupCollapsed('[oblist-shipping] ' + label + ' (' + Math.round(durationMs) + 'ms)');
        console.log('Request params:', params);
        console.log('display_mode  :', data && data.display_mode);
        console.log('curator_only  :', data && data.curator_only);
        console.log('from_price    :', data && data.from_price, data && data.currency);
        console.log('range         :', data && data.range);
        console.log('options       :', (data && data.options ? data.options.length : 0) + ' items');
        console.log('geo_level     :', data && data.geo_level);
        console.log('destination   :', data && data.destination);
        var notesFilled = [];
        if (data && data.notes) {
          for (var k in data.notes) {
            if (Object.prototype.hasOwnProperty.call(data.notes, k) && data.notes[k]) notesFilled.push(k);
          }
        }
        console.log('notes filled  :', notesFilled);
        console.log('full response :', data);
        console.groupEnd();
      } catch (_e) { /* swallow logging errors */ }
    }

    // ─── Network ──────────────────────────────────────────────────────────
    // Always attaches:
    //   • prodWeeksMin / prodWeeksMax — production lead time from metafields,
    //     so the API can compute arrives_from / arrives_to ISO dates server-side.
    //   • isMto / isVintage — Shopify-side product flags so the API doesn't
    //     send irrelevant copy (e.g. an MTO "your piece is being crafted now"
    //     note for a vintage one-of-a-kind product).
    //   • returnDays — store-level return window (14 in this theme; matches the
    //     `Number_days_return` i18n + the "RETURN & EXCHANGE WITHIN 14 DAYS"
    //     copy used elsewhere). Passed so the API can generate accurate
    //     return-policy text in commitments without hardcoding 30.
    function fetchEstimate(params) {
      var fullParams = Object.assign({
        prodWeeksMin: minWeeks,
        prodWeeksMax: maxWeeks,
        isMto: isMadeToOrder ? 'true' : 'false',
        isVintage: isVintage ? 'true' : 'false',
        returnDays: 14
      }, params || {});
      var qs = Object.keys(fullParams).map(function (k) {
        return encodeURIComponent(k) + '=' + encodeURIComponent(fullParams[k]);
      }).join('&');

      var t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      return fetch(API_URL + '?' + qs, { credentials: 'omit' })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var t1 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
          debugLogResponse(params.destPostal ? 'refine' : 'autoDetect', fullParams, data, t1 - t0);
          return data;
        });
    }

    // ─── Auto-detect on load ──────────────────────────────────────────────
    // Resolution priority for the destination:
    //   1. Markets country (the buyer's explicit Shopify selection — when they
    //      switch via the localization selector, this is their stated intent)
    //   2. IP geo (only when Markets isn't set)
    //
    // When both Markets and IP geo are available, we still pass useIpGeo so
    // the backend can refine to city/postal — but ONLY within the Markets
    // country. If the IP resolves to a different country, the backend should
    // ignore the IP geo and stay at country-only resolution.
    function autoDetect() {
      if (!productId) return;

      // Bots: skip the network call. Show a quiet vendor reassurance line.
      if (isBot()) {
        showNote(vendorFallback ? (t('shipping_from', 'Shipping from') + ' ' + vendorFallback) : '');
        return;
      }

      // Resolution priority for the destination, from strongest to weakest:
      //   1. Stored userDest with postal — buyer typed a postal on a previous
      //      PDP this session → reuse with full geo precision.
      //   2. Stored userDest without postal — buyer just picked a country in
      //      the modal dropdown → use country-only resolution.
      //   3. Markets country + IP geo → backend reconciles (full geo if IP
      //      matches Markets, country-only otherwise).
      //   4. IP geo only → legacy path when Markets isn't set.
      //
      // Conflict policy (Markets vs. userDest): if Markets is set and differs
      // from the stored userDest.country, Markets WINS — an explicit switch
      // via the localization header selector is a stronger signal than an
      // ad-hoc modal pick on a previous product (the buyer rebound their
      // currency / region intentionally). We also clear the stale userDest
      // so future navigations don't keep re-fighting Markets.
      var userDest = getUserDest();
      if (userDest && marketsCountry && userDest.country !== marketsCountry) {
        try { sessionStorage.removeItem(USER_DEST_KEY); } catch (_e) { /* silent */ }
        userDest = null;
      }
      var hasUserDest = !!(userDest && userDest.country);
      var hasUserDestPostal = !!(hasUserDest && userDest.postal);

      // Cache key includes Markets country + stored postal so switching
      // markets OR typing a new postal invalidates the cache automatically.
      // Country-only userDest goes in its own cache bucket (':co:<iso>') so
      // it doesn't collide with full-postal entries for the same country.
      var suffix;
      if (hasUserDestPostal) {
        suffix = 'auto:' + currency + ':' + marketsCountry + ':' + userDest.postal;
      } else if (hasUserDest) {
        suffix = 'auto:' + currency + ':' + marketsCountry + ':co:' + userDest.country;
      } else {
        suffix = 'auto:' + currency + ':' + marketsCountry;
      }
      var cached = getCached(productId, suffix);
      if (cached) { renderResult(cached); return; }

      showLoading();

      var params = { productId: productId, currency: currency };
      if (hasUserDestPostal) {
        // Buyer's explicit postal from a previous product this session.
        // Skip useIpGeo entirely — destPostal is authoritative.
        params.destCountry = userDest.country;
        params.destPostal = userDest.postal;
      } else if (hasUserDest) {
        // Country-only choice (buyer picked country in modal but never typed
        // a postal). Pass destCountry so the backend resolves country-only
        // without IP geo overriding the explicit pick.
        params.destCountry = userDest.country;
      } else if (marketsCountry) {
        params.destCountry = marketsCountry;
        params.useIpGeo = 'true';
      } else {
        params.useIpGeo = 'true';
      }

      fetchEstimate(params)
        .then(function (data) {
          if (data && data.success && data.from_price) {
            setCached(productId, suffix, data);
          }
          renderResult(data);
        })
        .catch(function () {
          // Network error: same rich fallback so the buyer never sees a dead state.
          renderRichFallback(null);
        });
    }

    autoDetect();
  }

  // ─── Lazy boot ──────────────────────────────────────────────────────────
  function lazyStart() {
    var target = document.getElementById('shipping-estimator');
    if (!target) return;

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            io.disconnect();
            init();
            return;
          }
        }
      }, { rootMargin: '200px' });
      io.observe(target);
    } else {
      setTimeout(init, 500);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', lazyStart);
  } else {
    lazyStart();
  }
})();
